import { ref, watch, nextTick } from "vue";
import SignaturePad from "signature_pad";

/**
 * Wraps the `signature_pad` canvas library with the mount-timing handling this platform's usage
 * needs: the canvas element is frequently gated behind more than one condition at once (a v-if
 * on the caller's side, a lazy-rendering parent like Vuetify's v-expansion-panel, or both), and
 * the initial signature value is often fetched asynchronously and can arrive after mount. Watch
 * the canvas ref itself (not a single gating condition) so init retries whenever the element
 * actually appears, and reload a late-arriving value without ever clobbering an in-progress draw.
 *
 * @param {object} options
 * @param {import('vue').Ref<string|null>} options.modelValue - current base64 signature image
 * @param {import('vue').Ref<boolean>} [options.disabled] - when true, drawing is disabled
 * @param {(value: string|null) => void} options.onChange - called with the new base64 image on
 *   stroke end, or null when cleared
 * @returns {{canvasRef: import('vue').Ref<HTMLCanvasElement|null>, clear: () => void}}
 */
export function useSignaturePad({ modelValue, disabled, onChange }) {
  const canvasRef = ref(null);
  const signaturePad = ref(null);

  function init() {
    if (signaturePad.value || !canvasRef.value) {
      return;
    }
    const canvas = canvasRef.value;
    signaturePad.value = new SignaturePad(canvas, {
      backgroundColor: "rgb(255, 255, 255)",
      penColor: "rgb(0, 0, 0)",
    });
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);

    if (modelValue.value) {
      signaturePad.value.fromDataURL(modelValue.value);
    }
    if (disabled?.value) {
      signaturePad.value.off();
    }

    signaturePad.value.addEventListener("endStroke", () => {
      onChange(signaturePad.value.toDataURL());
    });
  }

  watch(
    canvasRef,
    async (canvas) => {
      if (!canvas) {
        signaturePad.value?.off();
        signaturePad.value?.clear();
        signaturePad.value = null;
        return;
      }
      await nextTick();
      init();
    },
    { immediate: true }
  );

  watch(modelValue, (value) => {
    if (value && signaturePad.value && signaturePad.value.isEmpty()) {
      signaturePad.value.fromDataURL(value);
    }
  });

  if (disabled) {
    watch(disabled, (isDisabled) => {
      if (!signaturePad.value) {
        return;
      }
      if (isDisabled) {
        signaturePad.value.off();
      } else {
        signaturePad.value.on();
      }
    });
  }

  function clear() {
    signaturePad.value?.clear();
    onChange(null);
  }

  return { canvasRef, clear };
}
